import React, { useCallback, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';

import {
  CreateUpdateContactDto,
  GetClientDto,
  GetContactDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { useAuth } from '../../../common/hooks/useAuth';
import { PermissionsEnum } from '../../../common/pokCore/authorization/permissions';
import {
  convert,
  newContact,
  validate,
} from '../../../common/pokCore/contexts/ContactContext';

import { ContactEditor } from './ContactEditor';
import { ContactSearch } from './ContactSearch';

interface ContactTabProps {
  clientId: string;
  client: GetClientDto;
}

export const ContactTab: React.FC<ContactTabProps> = props => {
  const [show, setShow] = useState<boolean>(false);
  const [contact, setContact] = useState<GetContactDto>();
  const [refresh, setRefresh] = useState<boolean>(false);

  const pok = usePokCore();
  const auth = useAuth();
  const { t, tk } = useTranslation('clientLng');
  const lang = pok.getLanguage();

  const editable =
    auth.check(PermissionsEnum.Clients) || auth.check(PermissionsEnum.Surveys);

  const getData = useCallback(
    ((
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      pok.contacts.getAllByPortionForClient(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        props.clientId ? props.clientId : undefined,
        lang,
      )) as GridGetterFunction,
    [pok.contacts, props.clientId, lang],
  );

  const getCount = useCallback(
    ((filterText, filterColumns) =>
      pok.contacts.getAllCountForClient(
        filterText,
        filterColumns,
        props.clientId ? props.clientId : undefined,
      )) as GridCountFunction,
    [pok.contacts, props.clientId],
  );

  const handleAdd = () => {
    setShow(true);
    setContact(undefined);
  };

  const handleClose = () => {
    setShow(false);
  };

  const addEditContact = async (
    contactCU: CreateUpdateContactDto,
    id?: string,
  ) => {
    const status = await validate(contactCU);
    if (status.valid) {
      try {
        if (id) {
          await pok.contacts.update(id, contactCU);
        } else {
          await pok.contacts.create(contactCU);
        }
        setShow(false);
        setRefresh(!refresh);
        return {
          saved: true,
          errors: [],
        };
      } catch (response) {
        return {
          saved: false,
          errors: await responseErrors(response as ResponseError),
        };
      }
    } else {
      return {
        saved: false,
        errors: status.errors,
      };
    }
  };

  const handleClick = async (id: string) => {
    const getContact = await pok.contacts.getById(id);
    setContact(getContact);
    setShow(true);
  };

  return (
    <>
      {editable && (
        <Button variant="outline-primary" onClick={handleAdd}>
          {t(tk.contact.new)}
        </Button>
      )}
      <ContactSearch
        getData={getData}
        getCount={getCount}
        onRowClick={editable ? handleClick : undefined}
        refresh={refresh}
      />
      <ContactEditor
        id={contact?.id}
        client={props.client}
        contact={contact ? convert(contact) : newContact(props.clientId)}
        onCompleted={addEditContact}
        show={show}
        handleClose={handleClose}
      />
      <Alert variant="info" className="mb-3">
        {t(tk.contact.emailFallbackInfo)}
      </Alert>
    </>
  );
};
